<template>
  <div
    style="border: 2px dashed #aab9c2"
    class="border-2 border-dashed border-border-upload-excel w-112.5 h-36 justify-center flex mt-5 bg-surface-secondary rounded-sm dark:border-dark-border dark:bg-surface-secondary-dark"
    :class="[
      { 'pointer-events-none': disabled },
      { 'bg-dark-secondary!': disabled },
    ]"
  >
    <FileUpload
      name="documents"
      url="/api/upload"
      accept=".xlsx,.xls,.xlsm"
      :maxFileSize="5242880"
      :multiple="false"
      :auto="true"
      invalid-file-size-message="El tamaño del archivo debe ser menor a 5 MB, intente nuevamente."
      invalidFileTypeMessage="El formato del archivo no es válido, intente nuevamente."
      :customUpload="true"
      @select="onSelectedFiles"
      @drop.prevent
      :disabled="disabled"
    >
      <template #header="{ chooseCallback }">
        <div class="upload-header flex flex-col items-center">
          <font-awesome-icon
            icon="fa-solid fa-cloud-arrow-up"
            class="text-accent-sky-60 font-900 text-[19px] leading-5"
            :class="[{ 'text-dark-border!': disabled }]"
          />
          <CustomButton
            :class="[
              'w-42.25 h-8 ',
              {
                'bg-white dark:bg-surface-secondary-dark': !disabled,
                'bg-dark-secondary! text-dark-border! border-dark-border!':
                  disabled,
              },
            ]"
            class="mt-3 text-accent-sky-60! border-accent-sky-60! hover:bg-surface-tertiary! hover:text-accent-sky-60! text-[12px]! font-700 leading-4! tracking-[0.2px]"
            label="Subir documentos"
            @click="chooseCallback"
            variant="outlined"
          />
          <p
            class="mt-3 text-text-secondary font-500 text-[12px] leading-4 tracking-[0.2px] dark:text-dark-secondary"
            :class="[{ 'text-dark-border!': disabled }]"
          >
            o arrastra y suelta tu archivo aquí
          </p>
          <small
            class="mt-2 text-text-secondary font-500 text-[7px] leading-4 tracking-[0px] dark:text-dark-secondary"
            :class="[{ 'text-dark-border! ': disabled }]"
          >
            El tamaño del archivo debe estar dentro de los 5 MB
          </small>
        </div>
      </template>
      <template #content="{ messages }">
        <div v-if="messages && messages.length > 0">
          {{ handleShowMsg(messages[0]) }}
        </div>
      </template>
    </FileUpload>
  </div>
  <div
    v-if="errorShow"
    class="file-card w-112.5 bg-white p-3 rounded-lg shadow-xs mt-4 border"
  >
    <div class="flex items-center justify-between">
      <div class="flex">
        <font-awesome-icon
          icon="fa-solid fa-file-excel"
          class="text-text-error font-900 text-[19px] leading-5"
        />
        <div>
          <p
            class="text-[14px] text-text-error font-400 leading-6 tracking-[-0.4px] ml-2"
          >
            {{ errorMsg }}
          </p>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="data.file"
    class="file-card w-112.5 bg-white p-3 rounded-lg shadow-xs mt-4 border dark:bg-surface-primary dark:text-dark-secondary"
  >
    <div class="flex items-center justify-between">
      <div class="flex">
        <font-awesome-icon
          icon="fa-solid fa-file-excel"
          class="text-text-secondary font-900 text-[19px] leading-5 dark:text-dark-secondary"
        />
        <div>
          <p class="text-[14px] font-400 leading-6 tracking-[-0.4px] ml-2">
            {{ data.file.name }}
          </p>
          <p class="text-xs text-gray-500 dark:text-text-disabled">
            ({{ (data.file.size / 1048576).toFixed(2) }} MB)
          </p>
        </div>
      </div>
      <CustomButton
        icon="fa-solid fa-trash"
        iconClass=" text-[11px] font-900 "
        labelClass="text-text-title"
        buttonClass="w-[32px] h-[32px]  text-accent-sky-60! border-accent-sky-60! 
              hover:bg-surface-tertiary! hover:text-accent-sky-60! text-[12px]! font-700 
              leading-[16px]! tracking-[0.2px] "
        variant="outlined"
        @click="removeFile"
      />
    </div>

    <div class="flex items-center mt-2 w-107.5">
      <div
        v-if="totalSizePercent < 100"
        class="flex-1 bg-dark-border rounded-xs h-1"
      >
        <div
          class="bg-accent-sky-60 h-full rounded-xs transition-all duration-300"
          :style="{ width: `${totalSizePercent}%` }"
        ></div>
      </div>
      <div v-else>
        <a
          :href="fileUrl"
          target="_blank"
          class="text-accent-sky-60 text-[11px] leading-4 tracking-[0px] font-600"
          >Ver Archivo</a
        >
      </div>

      <div
        v-if="totalSizePercent < 100"
        class="ml-3 text-[11px] text-text-primary leading-4 tracking-[-0.2px] font-500 dark:text-dark-secondary"
      >
        {{ totalSizePercent }}%
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import CustomButton from "./CustomButton.vue";

const emits = defineEmits(["handleLoadData", "removeFile", "enableItem"]);

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const totalSize = ref(0);
const totalSizePercent = ref(0);
const fileUrl = ref<string>("");
const errorMsg = ref<string>("");
const errorShow = ref<boolean>(false);

async function onSelectedFiles(event: any) {
  errorShow.value = false;
  errorMsg.value = "";
  props.data.file = event.files[0];
  const blob = new Blob([props.data.file], { type: props.data.file.type });
  fileUrl.value = URL.createObjectURL(blob);

  try {
    const XLSX = await import("xlsx");
    const arrayBuffer = await blob.arrayBuffer();
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet);
    emits("handleLoadData", jsonData);
  } catch (error) {
    console.error("Error al cargar el archivo Excel:", error);
  }

  simulateUpload();
}

function simulateUpload() {
  totalSize.value = props.data.file.size;
  totalSizePercent.value = 0;

  let uploadInterval = setInterval(() => {
    if (totalSizePercent.value >= 100) {
      emits("enableItem", true);
      clearInterval(uploadInterval);
    } else {
      totalSizePercent.value += 20;
    }
  }, 500);
}

function removeFile() {
  props.data.file = null;
  fileUrl.value = "";
  totalSizePercent.value = 0;
  emits("removeFile");
}
function handleShowMsg(msg: string) {
  errorShow.value = true;
  errorMsg.value = msg;
}
</script>
